/**
 * Design Path Toggle System
 * Allows users to switch between Quick Design (Radio Selector) and Custom Design (PitchPrint Studio)
 * Ensures clean state transitions and proper section visibility
 */
(() => {
  'use strict';

  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[DesignPathToggle]', ...args);

  // Wait for StateManager to be available
  const initToggle = () => {
    if (!window.PitchPrintStateManager) {
      log('Waiting for StateManager...');
      setTimeout(initToggle, 100);
      return;
    }

    const stateManager = window.PitchPrintStateManager;
    const toggle = document.getElementById('design-path-toggle');

    if (!toggle) {
      log('Toggle element not found (product may not have design path enabled)');
      return;
    }

    const radioButtons = toggle.querySelectorAll('input[type="radio"][name="design_path"]');
    if (radioButtons.length === 0) {
      log('No radio buttons found in toggle');
      return;
    }

    log('Design path toggle initialized');

    // Get initial path from StateManager or default to 'quick'
    let currentPath = stateManager.getDesignPath ? stateManager.getDesignPath() : null;
    if (!currentPath) {
      currentPath = 'quick';
      if (stateManager.setDesignPath) {
        stateManager.setDesignPath(currentPath);
      }
    }

    // Set initial radio selection
    radioButtons.forEach(radio => {
      if (radio.value === currentPath) {
        radio.checked = true;
      }
    });

    // Update visibility based on initial path
    updateSectionVisibility(currentPath);

    log(`Initial design path: ${currentPath}`);

    // Handle toggle changes
    radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (!e.target.checked) return;

        const newPath = e.target.value;
        const oldPath = currentPath;

        if (newPath === oldPath) {
          log(`Already on ${newPath} path, no change needed`);
          return;
        }

        log(`Switching design path: ${oldPath} -> ${newPath}`);

        // Update state
        currentPath = newPath;
        if (stateManager.setDesignPath) {
          stateManager.setDesignPath(newPath);
        }

        // Update section visibility
        updateSectionVisibility(newPath);

        log(`Switched to ${newPath} path`);
      });
    });

    // Helper: Show/hide sections based on active path
    function updateSectionVisibility(activePath) {
      // Design Radio Selector (for Quick Design path)
      const radioSelector = document.querySelector('.design-radio-selector');

      // PitchPrint Section (for Custom Design path)
      const pitchprintSection = document.querySelector('.pitchprint-section');

      // Also check for confidence builder
      const confidenceBuilder = document.querySelector('.step2-confidence-builder');

      log(`Updating visibility for path: ${activePath}`);

      if (activePath === 'quick') {
        // Show Radio Selector, hide PitchPrint section
        if (radioSelector) {
          radioSelector.style.display = 'block';
          log('Radio selector shown');
        }

        if (pitchprintSection) {
          pitchprintSection.style.display = 'none';
          log('PitchPrint section hidden');
        }

        // Hide confidence builder when in quick mode (radio selector has its own flow)
        if (confidenceBuilder) {
          confidenceBuilder.style.display = 'none';
        }
      } else if (activePath === 'custom') {
        // Hide Radio Selector, show PitchPrint section
        if (radioSelector) {
          radioSelector.style.display = 'none';
          log('Radio selector hidden');
        }

        if (pitchprintSection) {
          pitchprintSection.style.display = 'block';
          log('PitchPrint section shown');
        }

        // Show confidence builder for custom design path
        if (confidenceBuilder) {
          confidenceBuilder.style.display = 'block';
        }
      }

      // Set data attribute on wrapper for CSS-based visibility as fallback
      const wrapper = document.querySelector('.product-configurator-wrapper');
      if (wrapper) {
        wrapper.setAttribute('data-design-path', activePath);
      }
    }

    // Listen for StateManager events to keep toggle in sync
    if (stateManager.on) {
      stateManager.on('design:path:changed', (event) => {
        const path = event.detail;
        log(`StateManager design path changed: ${path}`);

        // Update radio selection
        radioButtons.forEach(radio => {
          radio.checked = (radio.value === path);
        });

        // Update visibility
        updateSectionVisibility(path);
      });
    }

    // Expose public API
    window.DesignPathToggle = {
      getCurrentPath: () => currentPath,
      setPath: (path) => {
        const radio = toggle.querySelector(`input[value="${path}"]`);
        if (radio) {
          radio.click();
        }
      },
      refresh: () => {
        updateSectionVisibility(currentPath);
      }
    };

    log('Design path toggle system ready');
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToggle);
  } else {
    initToggle();
  }

  log('Design path toggle script loaded');
})();
